import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';

/**
 * Describes a single column in the data table.
 *
 * @example
 * const columns: DataTableColumn[] = [
 *   { key: 'name',    label: 'Name',    mono: false },
 *   { key: 'type',    label: 'Type',    mono: true  },
 *   { key: 'default', label: 'Default', mono: true  },
 *   { key: 'desc',    label: 'Description' },
 * ];
 */
export interface DataTableColumn {
  /** Property key used to look up the value in each row object. */
  key: string;
  /** Header label displayed in the thead. */
  label: string;
  /**
   * Render cell text in monospace font at a smaller size.
   * Useful for code-like values (types, defaults, tokens).
   * @default false
   */
  mono?: boolean;
  /**
   * Horizontal alignment of both the header and cell.
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Optional fixed or min width (any CSS value).
   * Applied as an inline style on the `<th>`.
   */
  width?: string;
}

/** A single row is a plain key→value record. */
export type DataTableRow = Record<string, string | number | boolean | null | undefined>;

/**
 * A lightweight, brand-styled data table component.
 *
 * Renders a responsive table with a tinted header row and divided body rows.
 * Designed for API reference tables, token lists, and other tabular data
 * throughout the UI Kit and documentation pages.
 *
 * @example
 * <app-data-table
 *   [columns]="[
 *     { key: 'prop',    label: 'Property',    mono: true },
 *     { key: 'type',    label: 'Type',        mono: true },
 *     { key: 'default', label: 'Default',     mono: true },
 *     { key: 'desc',    label: 'Description' }
 *   ]"
 *   [rows]="[
 *     { prop: 'color', type: 'string', default: '\'dark\'', desc: 'Brand color name' },
 *   ]"
 * />
 */
@Component({
  selector: 'app-data-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconButtonComponent],
  host: { class: 'block' },
  template: `
    <div class="overflow-x-auto rounded-xl border border-outline">
      <table class="w-full text-sm" role="table">
        <thead>
          <tr class="bg-surface-medium text-left">
            @for (col of columns(); track col.key) {
              <th
                class="px-4 py-3 font-semibold"
                [style.text-align]="col.align ?? 'left'"
                [style.width]="col.width ?? null"
                [style.min-width]="col.width ?? null"
              >
                {{ col.label }}
              </th>
            }
            @if (removableRows()) {
              <th class="px-2 py-3" style="width: 1%" aria-hidden="true"></th>
            }
          </tr>
        </thead>
        <tbody class="divide-y divide-[var(--ion-color-outline)]">
          @for (row of rows(); track $index) {
            <tr
              [class.cursor-pointer]="clickableRows()"
              [class.hover:bg-surface-low]="clickableRows()"
              [class.transition-colors]="clickableRows()"
              (click)="clickableRows() && rowClick.emit($index)"
              (keydown.enter)="clickableRows() && rowClick.emit($index)"
              [attr.tabindex]="clickableRows() ? 0 : null"
              [attr.role]="clickableRows() ? 'button' : null"
            >
              @for (col of columns(); track col.key) {
                <td
                  class="px-4 py-3"
                  [class.mono-text]="col.mono"
                  [class.text-xs]="col.mono"
                  [style.text-align]="col.align ?? 'left'"
                >
                  {{ row[col.key] ?? emptyValue() }}
                </td>
              }
              @if (removableRows()) {
                <td class="px-2 py-3 text-right" (click)="$event.stopPropagation()">
                  <app-icon-button
                    icon="close"
                    size="xxs"
                    color="danger"
                    [border]="true"
                    [ariaLabel]="removeRowAriaLabel()"
                    (click)="removeClick.emit($index)"
                  ></app-icon-button>
                </td>
              }
            </tr>
          }
          @if (rows().length === 0) {
            <tr>
              <td
                class="px-4 py-6 text-center text-muted"
                [attr.colspan]="totalColumnCount()"
              >
                {{ emptyMessage() }}
              </td>
            </tr>
          }
        </tbody>
        @if (summaryRow(); as summary) {
          <tfoot>
            <tr class="bg-surface-low font-bold border-t border-outline">
              @for (col of columns(); track col.key) {
                <td
                  class="px-4 py-3"
                  [style.text-align]="col.align ?? 'left'"
                >
                  {{ summary[col.key] ?? '' }}
                </td>
              }
              @if (removableRows()) {
                <td class="px-2 py-3" aria-hidden="true"></td>
              }
            </tr>
          </tfoot>
        }
      </table>
    </div>
  `,
})
export class DataTableComponent {
  /** Column definitions describing the header and how to render each cell. */
  readonly columns = input.required<DataTableColumn[]>();

  /** Array of row objects. Each row is a key→value record matching column keys. */
  readonly rows = input.required<DataTableRow[]>();

  /** Value shown for null / undefined cells. */
  readonly emptyValue = input<string>('\u2014'); // em dash

  /** Message shown when the rows array is empty. */
  readonly emptyMessage = input<string>('No data');

  /**
   * When true, rows render with a pointer cursor + hover background and the
   * `rowClick` output fires on click or Enter. Off by default to keep the
   * static-documentation usage (showcase / API reference tables) inert.
   */
  readonly clickableRows = input<boolean>(false);

  /** Emits the clicked row's index. Only fires when `clickableRows` is true. */
  readonly rowClick = output<number>();

  /**
   * When true, each row renders a trailing icon-button column with a danger
   * X icon. Clicks emit `removeClick` with the row index and do not
   * propagate to the row's click handler.
   */
  readonly removableRows = input<boolean>(false);

  /** Accessible label for the per-row remove button. */
  readonly removeRowAriaLabel = input<string>('Remove row');

  /** Emits the row index whose remove (X) button was clicked. */
  readonly removeClick = output<number>();

  /**
   * Optional summary row rendered in a `<tfoot>` at the bottom of the
   * table. Same shape as `rows` — each key matches a column. Rendered with
   * bold styling and a subtle background to set it apart from data rows.
   * Typical use: column totals (e.g. sum of an Annual Fee column).
   */
  readonly summaryRow = input<DataTableRow | undefined>(undefined);

  /** Total column count including the optional remove column. */
  readonly totalColumnCount = computed(
    () => this.columns().length + (this.removableRows() ? 1 : 0),
  );
}
